import {
    provideHttpClient,
    withFetch,
    withInterceptors
} from '@angular/common/http';
import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    provideAppInitializer
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
    provideRouter,
    Router,
    withEnabledBlockingInitialNavigation,
    withInMemoryScrolling
} from '@angular/router';

import { providePrimeNG } from 'primeng/config';
import { appRoutes } from './app.routes';
import { definePreset } from '@primeuix/themes';
import { withNgxsFormPlugin } from '@ngxs/form-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { httpErrorInterceptor } from './shared/interceptors/error-interceptor.service';
import { tokenInterceptor } from './shared/interceptors/token-interceptor.service';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { LoaderService } from './shared/services/loader.service';
import { AuthorizationService } from '@shared/services/authorization.service';
import {
    AddressGraphQLSchema,
    BranchGraphQLSchema,
    CityGraphQLSchema,
    CourierGraphQLSchema,
    CreditBalanceGraphQLSchema,
    CreditCardlinkResponseGraphQLSchema,
    CreditCardlinkResponseGraphQLSchemaActions,
    CreditContractGraphQLSchema,
    CreditGraphQLSchema,
    CustomerCalendarGraphQLSchema,
    CustomerCalendarServiceGraphQLSchema,
    CustomerCalendarServiceGraphQLSchemaActions,
    CustomerGraphQLSchema,
    DigitalClientCardGraphQLSchema,
    DiscountCategoryGraphQLSchema,
    ElectronicCashRegisterGraphQLSchema,
    ElectronicCashRegisterGraphQLSchemaActions,
    EmployeeGraphQLSchema,
    EntitySubjectInfoGraphQLSchema,
    FirstNameGraphQLSchema,
    GoodSaleGroupGraphQLSchema,
    GoodSaleItemGraphQLSchema,
    GoodSaleSummaryGraphQLSchema,
    InvoiceDetailGraphQLSchema,
    InvoiceGraphQLSchema,
    InvoiceIssuerGraphQLSchema,
    InvoicePaymentGraphQLSchema,
    MunicipalityGraphQLSchema,
    OccupationActivityGraphQLSchema,
    POSTerminalGraphQLSchema,
    POSTerminalResponseGraphQLSchema,
    ProductDepartmentGraphQLSchema,
    ProductGraphQLSchema,
    ProductLSCategoryGraphQLSchema,
    ProductLSSubCategoryGraphQLSchema,
    ProductPackageGraphQLSchema,
    ProductPackagePieceGraphQLSchema,
    ProductShelveGraphQLSchema,
    ProductUnitTypeGraphQLSchema,
    RouteGraphQLSchema,
    SupplierGraphQLSchema,
    TAXOfficeGraphQLSchema,
    TraderProfitMarginGraphQLSchema,
    UserAPIInfoGraphQLSchema,
    UserAPIInfoGraphQLSchemaState,
    VATCategoryGraphQLSchema
} from '@shared/zigra-schemas/zigra-schema.autogenerated.models';
import { enableMapSet } from 'immer';
import { PostDCLsToAADESchema } from '@shared/zigra-schemas/custom/PostDCLsToAADE.schema.service';

import { registerLocaleData } from '@angular/common';
import localeEl from '@angular/common/locales/el';
import { NoActionSchemaState } from '@shared/zigra-schemas/custom/no-action';
import { PostInvoicesToAADESchema } from '@shared/zigra-schemas/custom/post-invocies-to-aade.schema.service';
import { ZionMessagesService } from '@shared/services/ax-messages.service';
import { AuthorizeOrganizationsSchema } from '@shared/zigra-schemas/custom/authorize-organizations.service';
import { CardlinkService } from '@shared/card-link/cardlink.service';
import { DclCardService } from '@shared/services/dcl-card/dcl-card.service';
import { CarQueryService } from '@shared/services/carquery.service';
import Aura from '@primeuix/themes/aura';
import { ElectronicCashRegisterExSchema } from '@shared/zigra-schemas/custom/ElectronicCashRegisterEx.schema.service';
import * as Sentry from '@sentry/angular';
// import Lara from '@primeuix/themes/lara';
// import Nora from '@primeuix/themes/nora';
// import Material from '@primeuix/themes/material';

registerLocaleData(localeEl, 'el-GR');

const MyPreset = definePreset(Aura, {
    // semantic: {
    //     primary: {
    //         '50': '#fff7ed',
    //         '100': '#ffedd5',
    //         '200': '#fed7aa',
    //         '300': '#fdba74',
    //         '400': '#fb923c',
    //         '500': '#f97316',
    //         '600': '#ea580c',
    //         '700': '#c2410c',
    //         '800': '#9a3412',
    //         '900': '#7c2d12',
    //         '950': '#431407'
    //     },
    //     colorScheme: {
    //         light: {
    //             primary: {
    //                 color: '{primary.500}',
    //                 contrastColor: '#ffffff',
    //                 hoverColor: '{primary.600}',
    //                 activeColor: '{primary.700}'
    //             },
    //             highlight: {
    //                 background: '{primary.50}',
    //                 focusBackground: '{primary.100}',
    //                 color: '{primary.700}',
    //                 focusColor: '{primary.800}'
    //             }
    //         },
    //         dark: {
    //             primary: {
    //                 color: '{primary.400}',
    //                 contrastColor: '{surface.900}',
    //                 hoverColor: '{primary.300}',
    //                 activeColor: '{primary.200}'
    //             },
    //             highlight: {
    //                 background:
    //                     'color-mix(in srgb, {primary.400}, transparent 84%)',
    //                 focusBackground:
    //                     'color-mix(in srgb, {primary.400}, transparent 76%)',
    //                 color: 'rgba(255,255,255,.87)',
    //                 focusColor: 'rgba(255,255,255,.87)'
    //             }
    //         }
    //     }
    // }
});

enableMapSet();

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            appRoutes,
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'enabled'
            }),
            withEnabledBlockingInitialNavigation()
        ),
        provideHttpClient(
            withFetch(),
            withInterceptors([httpErrorInterceptor, tokenInterceptor])
        ),
        provideAnimationsAsync(),

        providePrimeNG({
            theme: {
                preset: MyPreset,
                options: {
                    // cssLayer: {
                    //     name: 'primeng',
                    //     order: 'theme, base, utilities, primeng'
                    // },
                    // cssLayer: {
                    //     name: 'primeng',
                    //     order: 'tailwind, primeng'
                    // },
                    darkModeSelector: '.app-dark'
                }
            }
        }),
        provideStore(),
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
        { provide: Sentry.TraceService, deps: [Router] },
        provideAppInitializer(() => {
            inject(Sentry.TraceService);
        }),
        // [],
        // withNgxsFormPlugin(),
        // withNgxsStoragePlugin({ keys: '*' })
        DialogService,
        MessageService,
        LoaderService,
        CardlinkService,
        AuthorizationService,
        ConfirmationService,
        ZionMessagesService,
        DclCardService,
        CarQueryService,
        // GraphQL Schemas
        PostDCLsToAADESchema,
        ElectronicCashRegisterExSchema,
        DigitalClientCardGraphQLSchema,
        //     PostInvoicesToAADESchema,
        // CommonSchema,
        NoActionSchemaState,
        SupplierGraphQLSchema,
        GoodSaleSummaryGraphQLSchema,
        GoodSaleGroupGraphQLSchema,
        GoodSaleItemGraphQLSchema,
        CustomerGraphQLSchema,
        ProductGraphQLSchema,
        ProductPackageGraphQLSchema,
        ProductPackagePieceGraphQLSchema,
        ProductShelveGraphQLSchema,
        AddressGraphQLSchema,
        MunicipalityGraphQLSchema,
        CityGraphQLSchema,
        DiscountCategoryGraphQLSchema,
        FirstNameGraphQLSchema,
        OccupationActivityGraphQLSchema,
        RouteGraphQLSchema,
        EmployeeGraphQLSchema,
        TAXOfficeGraphQLSchema,
        TraderProfitMarginGraphQLSchema,
        CourierGraphQLSchema,
        ProductUnitTypeGraphQLSchema,
        ProductLSCategoryGraphQLSchema,
        ProductLSSubCategoryGraphQLSchema,
        VATCategoryGraphQLSchema,
        BranchGraphQLSchema,
        InvoiceGraphQLSchema,
        InvoiceDetailGraphQLSchema,
        PostInvoicesToAADESchema,
        POSTerminalGraphQLSchema,
        InvoicePaymentGraphQLSchema,
        POSTerminalResponseGraphQLSchema,
        AuthorizeOrganizationsSchema,
        ProductDepartmentGraphQLSchema,
        EntitySubjectInfoGraphQLSchema,
        UserAPIInfoGraphQLSchema,
        InvoiceIssuerGraphQLSchema,

        CreditGraphQLSchema,
        CreditBalanceGraphQLSchema,
        CreditContractGraphQLSchema,
        CreditCardlinkResponseGraphQLSchema,

        ElectronicCashRegisterGraphQLSchema,
        CustomerCalendarGraphQLSchema,
        CustomerCalendarServiceGraphQLSchema
    ]
};
